import { useCallback } from "react";
import { QueryKey, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { BuyerCpvStatsQuery, CpvStatsRequest } from "lib/generated/app-service-gql/graphql";
import { useGraphQlClient, useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const BUYER_CPV_STATS_QUERY = graphql(`
  query buyerCpvStats($buyerId: String!, $options: CpvStatsRequest!) {
    buyerCpvStats(buyerId: $buyerId, options: $options) {
      overallStats {
        totalValue
        totalAwarded
        avgContractValue
        percentToSmes
      }
      perCpvStats {
        totalValue
        totalAwarded
        avgContractValue
        percentToSmes
        code
        name
      }
    }
  }
`);

/** Shared so the declarative and imperative paths hit the same cache entry. */
export function buyerCpvStatsKey(buyerId: string, options: CpvStatsRequest): QueryKey {
  return ["buyerCpvStats", buyerId, options];
}

export function useBuyerCpvStats(
  buyerId: string,
  options: CpvStatsRequest = { period: "P1Y", upcomingExpiries: false },
  queryOptions?: UseQueryOptions<BuyerCpvStatsQuery, unknown, BuyerCpvStatsQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    buyerCpvStatsKey(buyerId, options),
    BUYER_CPV_STATS_QUERY,
    [{ buyerId, options }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...queryOptions,
    },
  );

  return { data: data?.buyerCpvStats, ...rest };
}

/**
 * Imperative escape hatch for the admin report builder, which fetches on a user selection and
 * persists the result into the report section — a transaction, not a declarative read. Making that
 * declarative would mean firing `onChange` from an effect on every cache settle, which fights the
 * builder's dirty state.
 *
 * Shares the document, key and cache with `useBuyerCpvStats`.
 */
export function useFetchBuyerCpvStats() {
  const queryClient = useQueryClient();
  const { client } = useGraphQlClient();

  return useCallback(
    (buyerId: string, options: CpvStatsRequest) =>
      queryClient.fetchQuery({
        queryKey: buyerCpvStatsKey(buyerId, options),
        queryFn: () => client.request(BUYER_CPV_STATS_QUERY, { buyerId, options }),
        ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      }),
    [queryClient, client],
  );
}
